import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FilterListIcon from '@mui/icons-material/FilterList';
import GridViewIcon from '@mui/icons-material/GridView';
import HomeIcon from '@mui/icons-material/Home';
import ListIcon from '@mui/icons-material/List';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { deleteWorkspaceFile, FileItem, useWorkspaceFiles } from 'api/files';
import { useNotifications } from 'components/notify';
import { useFileUpload } from 'hooks/useFileUpload';
import { useUploadOptional } from 'providers/UploadProvider';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import FileEntity, { ListItemCell } from './components/FileEntity';
import FileMenu from './components/FileMenu';
import { FilePreviewModal } from './components/FilePreviewModal';
import { formatFileSize } from './utils/formatFileSize';
import { getFileType } from './utils/getFileType';

type FileExplorerProps = {
  onChange?: (v: unknown) => void;
  allowedExtensions?: string[];
};

export default function FileExplorer(props: FileExplorerProps) {
  const { onChange, allowedExtensions } = props;

  const { showNotification } = useNotifications();

  const allowedExtensionsMap = useMemo(
    () => new Set(allowedExtensions?.map((ext) => ext.toLowerCase())),
    [allowedExtensions],
  );

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [viewMenuAnchorEl, setViewMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  );

  // File upload: use UploadProvider when available, else local hook
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadContext = useUploadOptional();
  const fallbackUpload = useFileUpload();
  const { data: _files, mutate: refreshFiles } = useWorkspaceFiles();
  const [isDragOver, setIsDragOver] = useState(false);

  const isUploading =
    uploadContext?.isUploading ?? fallbackUpload.uploadProgress.uploading;
  const beginUploads = useCallback(
    (files: File[]) => {
      if (uploadContext) {
        uploadContext.beginUploadMany(files);
      } else {
        (async () => {
          for (const file of files) {
            await fallbackUpload.uploadFile(file);
          }
          fallbackUpload.resetProgress();
          refreshFiles();
        })();
      }
    },
    [uploadContext, fallbackUpload, refreshFiles],
  );

  // // State for context menu
  const [contextMenuAnchorEl, setContextMenuAnchorEl] =
    useState<null | HTMLElement>(null);

  // Track selected items for multi-selection and context menu
  const [selectedItems, setSelectedItems] = useState<FileItem[]>([]);

  const [focusedItem, setFocusedItem] = useState<FileItem | null>(null);

  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);

  useEffect(() => {
    function func(ev: KeyboardEvent) {
      if (ev.key === ' ') {
        // Don't prevent default if user is typing in an input field
        const target = ev.target as HTMLElement;
        if (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable
        ) {
          return;
        }

        ev.preventDefault();
        if (previewFile) {
          setPreviewFile(null);
        } else if (selectedItems.length) {
          setPreviewFile(selectedItems[0]);
        }
      }
    }
    window.addEventListener('keydown', func as any);

    return () => {
      window.removeEventListener('keydown', func as any);
    };
  }, [previewFile, selectedItems]);

  const selectedItemsMap = useMemo(
    () => new Set(selectedItems.map((item) => item.id)),
    [selectedItems],
  );

  const [searchQuery, setSearchQuery] = useState('');

  const files = useMemo(
    () =>
      _files?.filter(
        (x) =>
          !searchQuery ||
          x.name.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [_files, searchQuery],
  );

  // View menu handlers
  const handleViewMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setViewMenuAnchorEl(event.currentTarget);
  };

  // File upload handlers
  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      if (!fileList || fileList.length === 0) return;

      const files = Array.from(fileList);
      beginUploads(files);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [beginUploads],
  );

  const handleRefresh = useCallback(() => {
    refreshFiles();
  }, [refreshFiles]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isDragOver to false if we're leaving the main container
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length === 0) return;

      beginUploads(files);
    },
    [beginUploads],
  );

  return (
    <Stack sx={{ height: 500, maxWidth: 1100 }}>
      <Paper
        elevation={1}
        sx={{
          height: '100%',
          border: '1px solid',
          borderColor: isDragOver ? 'primary.main' : 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          bgcolor: isDragOver ? 'action.hover' : 'background.paper',
          transition: 'border-color 0.2s, background-color 0.2s',
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Toolbar variant="dense" sx={{ minHeight: '48px' }}>
            {/* Navigation buttons */}
            <IconButton disabled size="small" sx={{ mr: 1 }} aria-label="back">
              <ArrowBackIcon />
            </IconButton>
            <IconButton
              disabled
              size="small"
              sx={{ mr: 1 }}
              aria-label="forward"
            >
              <ArrowForwardIcon />
            </IconButton>
            <IconButton disabled size="small" sx={{ mr: 2 }} aria-label="home">
              <HomeIcon />
            </IconButton>

            {/* URL/Path bar */}
            <TextField
              size="small"
              placeholder="Location path"
              variant="outlined"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                endAdornment: (
                  <IconButton size="small" onClick={handleRefresh}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                ),
              }}
              sx={{ mr: 2, flexGrow: 1 }}
            />

            {/* File upload button */}
            <Tooltip title="Upload File">
              <span>
                <IconButton
                  size="small"
                  sx={{ mr: 1 }}
                  onClick={handleUploadClick}
                  disabled={isUploading}
                  color="primary"
                >
                  {isUploading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <CloudUploadIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileSelected}
              multiple
            />

            {/* Favorites button */}
            <Tooltip title="Favorites">
              <IconButton
                disabled
                size="small"
                sx={{ mr: 2 }}
                aria-label="favorites"
              >
                <StarIcon />
              </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

            {/* View settings */}
            <Tooltip title="View Options">
              <IconButton
                size="small"
                sx={{ mr: 2 }}
                aria-label="view options"
                color="primary"
                onClick={handleViewMenuClick}
                aria-controls="view-menu"
                aria-haspopup="true"
              >
                {viewMode === 'grid' ? <GridViewIcon /> : <ListIcon />}
              </IconButton>
            </Tooltip>
            <Menu
              id="view-menu"
              anchorEl={viewMenuAnchorEl}
              keepMounted
              open={Boolean(viewMenuAnchorEl)}
              onClose={() => setViewMenuAnchorEl(null)}
            >
              <MenuItem
                onClick={() => {
                  setViewMode('grid');
                  setViewMenuAnchorEl(null);
                }}
              >
                <ListItemIcon>
                  <GridViewIcon
                    color={viewMode === 'grid' ? 'primary' : 'inherit'}
                  />
                </ListItemIcon>
                <ListItemText primary="Grid View" />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setViewMode('list');
                  setViewMenuAnchorEl(null);
                }}
              >
                <ListItemIcon>
                  <ListIcon
                    color={viewMode === 'list' ? 'primary' : 'inherit'}
                  />
                </ListItemIcon>
                <ListItemText primary="List View" />
              </MenuItem>
            </Menu>

            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

            {/* Filter button */}
            <Tooltip title="Filter">
              <IconButton disabled size="small" aria-label="filter">
                <FilterListIcon />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </Box>

        {viewMode === 'list' && (
          <Grid container pr={1}>
            <Stack direction="row" sx={{ width: '100%' }} px={2} py={0.5}>
              <ListItemCell>Icon</ListItemCell>
              <ListItemCell xs={5}>Name</ListItemCell>
              <ListItemCell>Size</ListItemCell>
              <ListItemCell xs={2}>Created At</ListItemCell>
              <ListItemCell xs={2}>Modified At</ListItemCell>
              <ListItemCell>Status</ListItemCell>
            </Stack>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
        )}
        <Box
          sx={{ p: 0, flexGrow: 1, overflow: 'auto', position: 'relative' }}
          onClick={() => {
            if (focusedItem) {
              setFocusedItem(null);
            } else {
              setSelectedItems([]);
            }
          }}
        >
          {isDragOver && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1000,
                bgcolor: 'rgba(25, 118, 210, 0.08)',
                border: '2px dashed',
                borderColor: 'primary.main',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              <Typography variant="h6" color="primary.main">
                Drop files here to upload
              </Typography>
            </Box>
          )}
          <Grid container>
            {files?.map((file) => {
              const itemId = file.id;
              return (
                <FileEntity
                  key={itemId}
                  file={file}
                  variant="file"
                  disabled={
                    allowedExtensions
                      ? !allowedExtensionsMap.has(
                          file.name.split('.').pop()?.toLowerCase() ?? '',
                        )
                      : false
                  }
                  fileType={getFileType(file.name)}
                  viewMode={viewMode}
                  selected={selectedItemsMap.has(itemId)}
                  focused={focusedItem?.id === itemId}
                  onClick={() => {
                    setFocusedItem(file);
                    setSelectedItems([file]); // Also select the item that was clicked
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenuAnchorEl(e.currentTarget as any);
                    setFocusedItem(file);
                  }}
                />
              );
            })}
          </Grid>
        </Box>

        <Stack
          p={1}
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          sx={{
            borderWidth: 1,
            borderTopStyle: 'solid',
            borderColor: 'divider',
          }}
        >
          <Button
            disabled={!selectedItems.length}
            variant="outlined"
            size="small"
            onClick={() => onChange?.(selectedItems[0])}
            sx={{
              lineHeight: 1,
              px: 3,
              py: 1,
            }}
          >
            Select
          </Button>
        </Stack>
        <Stack
          gap={2}
          px={2}
          direction="row"
          alignItems="center"
          sx={{
            borderWidth: 1,
            borderTopStyle: 'solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="caption">
            {files?.length ?? 0} items{' '}
            {searchQuery ? `(${_files?.length ?? 0} total)` : ''}
          </Typography>
          {selectedItems.length > 0 && (
            <>
              <Divider orientation="vertical" flexItem />
              <Typography variant="caption">
                {selectedItems.length} items selected
              </Typography>
              <Typography variant="caption">
                {formatFileSize(
                  selectedItems?.reduce((acc, c) => acc + +c.size, 0),
                )}
              </Typography>
            </>
          )}
        </Stack>

        {/* The context menu is now handled by the FileMenu component in FileExplorerContent */}
      </Paper>
      <FileMenu
        file={focusedItem}
        anchorEl={contextMenuAnchorEl}
        onClose={() => {
          setContextMenuAnchorEl(null);
        }}
        onPreview={() => {
          if (focusedItem) {
            setPreviewFile(focusedItem);
          }
          setContextMenuAnchorEl(null);
        }}
        onDelete={async () => {
          const file = focusedItem;
          if (!file) return;

          try {
            await deleteWorkspaceFile(file?.workspace_id, file.id);
            showNotification('File deleted successfully', {
              variant: 'success',
            });
          } catch (error) {
            console.error('Failed to delete file:', error);
            showNotification('Failed to delete file', {
              variant: 'error',
            });
          } finally {
            setContextMenuAnchorEl(null);
            setSelectedItems([]);
            setFocusedItem(null);
            refreshFiles();
          }
        }}
      />
      <FilePreviewModal
        previewFile={previewFile}
        onClose={() => setPreviewFile(null)}
      />
    </Stack>
  );
}
